import { Preferences } from '@capacitor/preferences';
import { KeychainAccess, SecureStorage, StorageErrorType } from '@aparajita/capacitor-secure-storage';
import * as Sentry from '@sentry/react';

const ACCESS_TOKEN_KEY = 'a1_access_token';
const REFRESH_TOKEN_KEY = 'a1_refresh_token';
const SECURE_STORE_READY_KEY = 'a1_secure_store_ready';

const SYNC_TO_ICLOUD = false;
const CONVERT_DATE = false;
const KEYCHAIN_ACCESS = KeychainAccess.afterFirstUnlockThisDeviceOnly;

const MAX_STORAGE_READ_ATTEMPTS = 2;

let accessToken: string | null = null;
let refreshToken: string | null = null;

let failedReadAttempts = 0;

const storageStateListeners = new Set<() => void>();

export const getAccessToken = (): string | null => accessToken;

export const getRefreshToken = (): string | null => refreshToken;

export const isStorageUnavailable = (): boolean => failedReadAttempts > 0;

export const hasExhaustedStorageRetries = (): boolean => failedReadAttempts >= MAX_STORAGE_READ_ATTEMPTS;

export const subscribeToStorageState = (listener: () => void): (() => void) => {
  storageStateListeners.add(listener);
  return () => storageStateListeners.delete(listener);
};

const setStorageState = (unavailable: boolean): void => {
  const wasExhausted = hasExhaustedStorageRetries();

  failedReadAttempts = unavailable ? failedReadAttempts + 1 : 0;

  if (hasExhaustedStorageRetries() !== wasExhausted) {
    storageStateListeners.forEach((listener) => listener());
  }
};

const errorCodeOf = (error: unknown): string | undefined => (error as { code?: string } | null)?.code;

const reportStorageFailure = (message: string, error: unknown): void => {
  console.error(message, error);
  Sentry.captureException(error, { tags: { section: 'token_storage' } });
};

const persistToken = async (key: string, value: string): Promise<boolean> => {
  try {
    await SecureStorage.set(key, value, CONVERT_DATE, SYNC_TO_ICLOUD, KEYCHAIN_ACCESS);
    return true;
  } catch (error) {
    reportStorageFailure(`Unable to persist ${key} to secure storage, the session will not survive a relaunch:`, error);
    return false;
  }
};

const readToken = async (key: string): Promise<{ value: string | null; failed: boolean }> => {
  try {
    const value = await SecureStorage.get(key, CONVERT_DATE, SYNC_TO_ICLOUD);
    return { value: typeof value === 'string' ? value : null, failed: false };
  } catch (error) {
    reportStorageFailure(`Unable to read ${key} from secure storage:`, error);

    if (errorCodeOf(error) === StorageErrorType.invalidData) {
      await SecureStorage.remove(key, SYNC_TO_ICLOUD).catch((removeError: unknown) => {
        console.error(`Unable to discard the corrupt value at ${key}:`, removeError);
      });

      return { value: null, failed: false };
    }

    return { value: null, failed: true };
  }
};

/**
 * @returns {Promise<boolean>} whether the secure store is safe to read from.
 */
const prepareSecureStoreForThisInstall = async (): Promise<boolean> => {
  try {
    const { value } = await Preferences.get({ key: SECURE_STORE_READY_KEY });
    if (value) return true;

    await SecureStorage.clear(SYNC_TO_ICLOUD);
    await Preferences.set({ key: SECURE_STORE_READY_KEY, value: '1' });
    return true;
  } catch (error) {
    reportStorageFailure('Unable to purge the secure store on first run, skipping the token read:', error);
    return false;
  }
};

const removeLegacyPlaintextTokens = async (): Promise<void> => {
  try {
    await Promise.all([Preferences.remove({ key: ACCESS_TOKEN_KEY }), Preferences.remove({ key: REFRESH_TOKEN_KEY })]);
  } catch (error) {
    console.error('Unable to remove the legacy plaintext tokens from Preferences:', error);
  }
};

const loadTokens = async (): Promise<void> => {
  if (!(await prepareSecureStoreForThisInstall())) {
    setStorageState(true);
    return;
  }

  const [access, refresh] = await Promise.all([readToken(ACCESS_TOKEN_KEY), readToken(REFRESH_TOKEN_KEY)]);

  if (!access.failed) accessToken = access.value;
  if (!refresh.failed) refreshToken = refresh.value;

  setStorageState(access.failed || refresh.failed);
};

/**
 * Updates the in-memory tokens and persists them to secure storage.
 * Pass `null` for a token to leave the existing value untouched.
 */
export const setTokens = async ({
  accessToken: newAccessToken,
  refreshToken: newRefreshToken
}: {
  accessToken?: string | null;
  refreshToken?: string | null;
}): Promise<void> => {
  const writes: Promise<boolean>[] = [];

  if (newAccessToken != null) {
    accessToken = newAccessToken;
    writes.push(persistToken(ACCESS_TOKEN_KEY, newAccessToken));
  }

  if (newRefreshToken != null) {
    refreshToken = newRefreshToken;
    writes.push(persistToken(REFRESH_TOKEN_KEY, newRefreshToken));
  }

  const written = await Promise.all(writes);

  if (written.length > 0 && written.every(Boolean)) setStorageState(false);
};

/** Clears the in-memory tokens and removes them from secure storage. */
export const clear = async (): Promise<void> => {
  accessToken = null;
  refreshToken = null;
  setStorageState(false);

  try {
    await Promise.all([SecureStorage.remove(ACCESS_TOKEN_KEY, SYNC_TO_ICLOUD), SecureStorage.remove(REFRESH_TOKEN_KEY, SYNC_TO_ICLOUD)]);
  } catch (error) {
    reportStorageFailure('Unable to remove tokens from secure storage:', error);
  }
};

/** Loads persisted tokens from secure storage into memory. Call once at app boot before render. */
export const hydrate = async (): Promise<void> => {
  await loadTokens();

  void removeLegacyPlaintextTokens();
};

export const retryHydrate = (): Promise<void> => loadTokens();
